import { observer } from "mobx-react";
import Card from "../../../../../components/layouts/card/card";
import { RETURN_METHOD_TYPE } from "../../../return-method-types";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import ReturnMethodStore from "../../../stores/return-method-store";
import FormFieldLineLayout from "../../../../../components/layouts/form-field-line-layout/form-field-line-layout";
import TranslationRichTextEditor from "../../../../../translation/translation-rich-text-editor/translation-rich-text-editor";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import dashboardSettingsStore from "../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../models/ProfileCapabilities";


interface IShippingInstructionsProps {
    returnMethod?: ReturnMethodStore,
}

interface INote {
    title: string;
    description: string;
    isHtml: boolean;
}


function getNoteForReturnMethod(returnMethod: ReturnMethodStore, getTranslation: (key: string, scope: string) => string, scope: string): INote | undefined {
    

    if (returnMethod?.methodType === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED) {
        return;
    }

    const note = {
        title: getTranslation("USING_VARIABLES", scope),
        description: getTranslation('VARIABLES_NOTE_1', scope),
        isHtml: true,
    };

    switch (returnMethod?.methodType as RETURN_METHOD_TYPE) {
        case RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL:
            note.description += getTranslation('VARIABLES_NOTE_2', scope);
            break;
        case RETURN_METHOD_TYPE.DROP_OFF_LOCATION:
            note.description += getTranslation('VARIABLES_NOTE_3', scope);
            break;
        case RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER:
            note.description += getTranslation('VARIABLES_NOTE_4', scope);
            break;
        default:
            break;
    }

    return note;
}


const ShippingInstructionsCard: React.FC<IShippingInstructionsProps> = observer(({returnMethod}: IShippingInstructionsProps) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-return-method-editor';

    if (!returnMethod) {
        return null;
    }

    /** Hide the entire card if content editing is not supported for Ship with any carrier */
    if (returnMethod?.methodType === RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER &&
        !dashboardSettingsStore
            .resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_ANY_CARRIER)
            .isContentSupported
    ){
        return null;
    }

    /** Hide the entire card if content editing is not supported for Drop-off location */
    if (returnMethod?.methodType === RETURN_METHOD_TYPE.DROP_OFF_LOCATION &&
        !dashboardSettingsStore
            .resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_RETURN_TO_DROP_OFF_LOCATION)
            .isContentSupported
    ){
        return null;
    }

    /** Hide the entire card if shipping instructions is not supported for No Shipment Required */
    if (returnMethod?.methodType === RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED &&
        !dashboardSettingsStore
            .resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_NO_SHIPMENT_REQUIRED)
            .isShippingInstructionsSupported
    ){
        return null;
    }

    /** Hide the entire card if shipping instructions is not supported for Ship with pre-paid label */
    if (returnMethod?.methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL &&
        !dashboardSettingsStore
            .resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_PREPAID_LABEL)
            .isShippingInstructionsSupported
    ){
        return null;
    }

    return(
        <Card
            toolbar={{
                title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation("SHIPPING_INSTRUCTIONS", scope)}</FontBody1>,
            }}
            isExpanded={false}
            note={getNoteForReturnMethod(returnMethod, getTranslation, scope)}
        >
            <FontBody2>{getTranslation("SHIPPING_INSTRUCTIONS_NOTE", scope)}</FontBody2>
            <FormFieldLineLayout>
                <div>
                    <FontBody2 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>{getTranslation("CONTENT", scope)}</FontBody2>
                    <TranslationRichTextEditor translationKey={returnMethod.instruction} disabled={!returnMethod.areInstructionsEditable()} pastePlain={true}/>
                </div>
            </FormFieldLineLayout>
        </Card>
    );
});

export default ShippingInstructionsCard;
